import React, { useEffect, useState } from 'react'
import Drawer from '@material-ui/core/Drawer'
import CssBaseline from '@material-ui/core/CssBaseline'
import AppBar from '@material-ui/core/AppBar'
import List from '@material-ui/core/List'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import TopBar from '../topbar'
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@material-ui/core'
import * as paths from '../../constants/route-paths'
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Avatar from '@material-ui/core/Avatar'
import { saveProjectType, removeProjectType } from '../../actions/project-management.action'
import { showMobileDrawer as showMobileDrawerAPI, setSidebarCollapsed } from '../../actions/common.action'
import { getGlobalSettingReducer } from '../../actions/global-setting.action'
import { logout } from '../../actions/user-management.action'
import Cookies from 'js-cookie'
import idx from 'idx'
import { Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import { navLinks } from './links'
import { lgStyles, mdStyles } from './styles'
import { setCookie } from '../../util/auth'
/**
 * HomeLayout Component
 * 
 * A responsive, flexible layout component that provides the structural foundation for the
 * Contractable application. Combines a top bar, collapsible sidebar with dynamic navigation,
 * and main content area. It adapts seamlessly to both desktop and mobile views with integrated
 * user profile, permissions-based links, and route handling.
 * 
 * @Author Kalaiselvan
 * @Created 2024-02-20
 * @Modified by Naseema- 2025-06-30 - Added data properties for cypress test cases-CTBL-275
 * 
 * MAJOR FEATURES:
 * 1. Responsive Layout:
 *    - Uses Material-UI’s Drawer, AppBar, and layout elements
 *    - Dynamically switches between permanent and temporary sidebar modes based on screen width
 *    - Expands sidebar automatically on mobile navigation
 * 
 * 2. Collapsible Sidebar:
 *    - Sidebar toggles between collapsed and expanded states on desktop
 *    - Stores and syncs sidebar collapsed state to Redux
 *    - Renders links dynamically based on permissions and entitlements
 *    - Uses tooltips in collapsed mode for improved UX
 * 
 * 3. Navigation and Routing:
 *    - Integrates with `react-router-dom` for route navigation
 *    - Handles route changes with query parameter parsing (e.g., `project-type`)
 *    - Auto-updates the document title based on account initials
 * 
 * 4. User Profile and Menu:
 *    - User menu with Profile, Reset Password, and Logout options
 *    - Supports avatar display and selected account info
 *    - Account switching via sidebar or user profile accordion
 * 
 * 5. Mobile Drawer Support:
 *    - Drawer opens/closes based on Redux `showMobileDrawer` state
 *    - Automatically closes drawer on navigation or action click
 * 
 * UI/UX IMPROVEMENTS:
 * 1. Layout:
 *    - Responsive design with seamless collapse/expand transitions
 *    - Dynamic styling via `lgStyles` and `mdStyles` based on screen size
 *    - Custom sidebar logo that switches between full and compact versions
 * 
 * 2. Interaction:
 *    - Smooth toggling with icons (`ChevronLeftIcon`, `ChevronRightIcon`)
 *    - Tooltips for collapsed menu items to preserve accessibility
 *    - Accordion-based nested menus for expandable navigation groups
 * 
 * 3. User Experience:
 *    - Personalized navigation with user’s name, role, and selected account
 *    - Persistent sidebar behavior across screen sizes
 *    - Mobile-optimized sidebar profile section
 * 
 * TECHNICAL ENHANCEMENTS:
 * 1. Redux Integration:
 *    - Retrieves user data, project type, entitlements, and mobile drawer state from Redux
 *    - Dispatches actions like `logout`, `saveProjectType`, `setSidebarCollapsed`, and more
 * 
 * 2. Cookie & Auth Management:
 *    - Uses `Cookies` and `setCookie` for storing `projectType` and session state
 *    - Clears cookies and resets state on logout
 * 
 * 3. Extensibility:
 *    - Sidebar navigation built using configurable `navLinks` array
 *    - Easily extendable with new routes, permission logic, and nested accordion menus
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.user - Current logged-in user information
 * @param {Object} props.projectType - Current selected project type (from Redux)
 * @param {Array} props.projectCategories - List of available project categories
 * @param {Array} props.entitlements - User entitlements
 * @param {boolean} props.showMobileDrawer - Redux flag for controlling mobile drawer visibility
 * @param {Function} props.saveProjectType - Redux action to save project type
 * @param {Function} props.removeProjectType - Redux action to remove project type
 * @param {Function} props.showMobileDrawerAPI - Redux action to toggle mobile drawer
 * @param {Function} props.setSidebarCollapsed - Redux action to set sidebar collapsed state
 * @param {Function} props.logout - Redux action to log the user out
 * @param {React.ReactNode} props.children - Main content passed to layout
 * @returns {JSX.Element} Full responsive layout including sidebar, top bar, and main content
 */

function HomeLayout(props) {

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isDesktopView, setIsDesktopView] = useState(window?.innerWidth > 1024);
    const lgStyle = lgStyles({ isCollapsed });
    const mdStyle = mdStyles();
    const [classes, setClasses] = useState(isDesktopView ? lgStyle : mdStyle)
    const navigate = useNavigate();
    const location = useLocation();
    const [variant, setVariant] = useState(isDesktopView ? 'permanent' : 'temporary')
    const [anchorEl, setAnchorEl] = React.useState(null)
    const isMenuOpen = Boolean(anchorEl)
    const { name, role } = props.user
    const { user } = props

    const setWindowDimensions = () => {
        const desktop = window.innerWidth > 1024;
        setIsDesktopView(desktop);
        setClasses(desktop ? lgStyle : mdStyle);
        setVariant(desktop ? 'permanent' : 'temporary');

        // When switching to mobile view, ensure sidebar is expanded
        if (!desktop && isCollapsed) {
            setIsCollapsed(false);
        }
    }

    useEffect(() => {
        setWindowDimensions();
        window.addEventListener('resize', setWindowDimensions);

        // Initialize the Redux state with the current sidebar state
        props.setSidebarCollapsed(isCollapsed);

        return () => {
            window.removeEventListener('resize', setWindowDimensions)
        }
    }, [])

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlProjectType = queryParams.get('project-type');
        if (urlProjectType && urlProjectType !== props.projectType) {
            props.saveProjectType(urlProjectType);
            setCookie({ name: 'projectType', value: urlProjectType });
        }
    }, [location.search, props.projectType]);

    useEffect(() => {
        if (isDesktopView) {
            setClasses(lgStyle);
        }
    }, [isCollapsed, lgStyle, isDesktopView]);

    const handleToggleCollapse = () => {
        if (isDesktopView) {
            const newCollapsedState = !isCollapsed;
            setIsCollapsed(newCollapsedState);
            props.setSidebarCollapsed(newCollapsedState);
        }
    };

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMobileMenuClose = () => {
        setAnchorEl(null)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        handleMobileMenuClose()
    }

    const logout = () => {
        handleMenuClose()
        props.logout()
        props.showMobileDrawerAPI(false)
        navigate('/login')
    }

    const editProfile = () => {
        handleMenuClose()
        props.showMobileDrawerAPI(false)

        // Expand the sidebar if it's currently collapsed
        if (isCollapsed && isDesktopView) {
            setIsCollapsed(false)
        }

        navigate('/profile')
    }

    const resetPassword = () => {
        handleMenuClose()
        props.showMobileDrawerAPI(false)

        // Expand the sidebar if it's currently collapsed
        if (isCollapsed && isDesktopView) {
            setIsCollapsed(false)
        }

        navigate('/profile/reset-password')
    }

    const changeAccount = () => {
        handleMenuClose()

        // Expand the sidebar if it's currently collapsed
        if (isCollapsed && isDesktopView) {
            setIsCollapsed(false)
        }

        navigate(`/${paths.GLOBAL_ACCOUNT}`)
    }

    const permissions = props.user.permissions
    const projectType = props.projectType

    if (props.user.selected_account.name) {
        let takingFirstLetters = (props.user.selected_account.name).match(/\b(\w)/g);
        let joiningFirstLetters = takingFirstLetters.join('');
        document.title = joiningFirstLetters + ' | Contractable'
    } else {
        document.title = 'Contractable'
    }

    const sidebarLinks = navLinks({ projectCategories: props.projectCategories, entitlements: props.entitlements, user, permissions })

    const changeUrl = async path => {
        const queryString = path.split('?')[1]
        const queryParams = new URLSearchParams(queryString)
        if (queryParams.has('project-type')) {
            await props.saveProjectType(queryParams.get('project-type'))
            setCookie({ name: 'projectType', value: queryParams.get('project-type') })
        } else {
            props.removeProjectType('')
            Cookies.remove('projectType')
        }

        // Expand the sidebar if it's currently collapsed in desktop view
        if (isCollapsed && isDesktopView) {
            setIsCollapsed(false)
        }

        // Close mobile drawer
        if (!isDesktopView) {
            props.showMobileDrawerAPI(false)
        }

        navigate(path)
    }

    // Handle accordion click or expand
    const handleAccordionClick = () => {
        // Expand the sidebar if it's currently collapsed in desktop view
        if (isCollapsed && isDesktopView) {
            setIsCollapsed(false);
        }
    };

    const menuId = 'primary-search-account-menu'
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem key={'editProfile'} onClick={editProfile}>Profile</MenuItem>
            <MenuItem key={'resetPassword'} onClick={resetPassword}>Reset Password</MenuItem>
            <MenuItem key={'logout'} onClick={logout}>Logout</MenuItem>
        </Menu>
    )

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar position='fixed' className={
                isDesktopView
                    ? `${classes.appBar} Homelayout ${isCollapsed ? 'collapsed-sidebar' : ''}`
                    : 'Homelayout mobile-view'
            }>
                <TopBar {...props} openHandler={() => { props.showMobileDrawerAPI(!props.showMobileDrawer) }} />
            </AppBar>
            <Drawer
                className={
                    isDesktopView
                        ? `${classes.drawer} mainDrawer sidebar-light ${isCollapsed ? 'collapsed' : ''}`
                        : 'mainDrawer sidebar-light mobile-drawer'
                }
                variant={variant}
                open={isDesktopView ? true : props.showMobileDrawer}
                classes={{
                    paper: isDesktopView ? classes.drawerPaper : classes.drawerPaperMobile,
                    root: isDesktopView && isCollapsed ? 'drawer-collapsed' : ''
                }}
                anchor='left'
                onClose={() => {
                    if (!isDesktopView) {
                        props.showMobileDrawerAPI(false);
                    }
                }}
            >
                {renderMenu}

                <div className="sidebar-container">
                    <div className="sidebar-header">
                        {!isDesktopView && (
                            <div className='sideBarCloseBtn'>
                                <IconButton color='inherit' onClick={() => { props.showMobileDrawerAPI(false) }}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        )}
                        <div className={classes.sidebarLogo}>
                            {!isCollapsed || !isDesktopView ? (
                                <>
                                    <div className={classes.logoContainer}>
                                        <img
                                            src={require('../../assets/logo.svg').default}
                                            alt='Logo'
                                            className={classes.logo}
                                        />
                                    </div>
                                    {isDesktopView && (
                                        <IconButton
                                            color="primary"
                                            aria-label="toggle drawer"
                                            onClick={handleToggleCollapse}
                                            className={`${classes.toggleButton} desktop-only-toggle`}
                                            classes={{
                                                root: classes.collapseButtonContainer
                                            }}
                                        >
                                            <ChevronLeftIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </>
                            ) : (
                                <div className={classes.collapsedButtonContainer}>
                                    <img
                                        src={require('../../assets/contractable-c.svg').default}
                                        alt='Logo'
                                        className={classes.collapsedLogo}
                                    />
                                    {isDesktopView && (
                                        <IconButton
                                            color="primary"
                                            aria-label="toggle drawer"
                                            onClick={handleToggleCollapse}
                                            className={`${classes.toggleButton} desktop-only-toggle`}
                                            classes={{
                                                root: classes.collapsedToggleButton
                                            }}
                                        >
                                            <ChevronRightIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            )}
                        </div>

                        {!isDesktopView && (
                            <div className="mobile-profile-section">
                                <Avatar alt={name} className={classes.userAvatar} src={require('../../assets/images/avatar/1.jpg').default} />
                                <div className="profile-details">
                                    <p className="name">{name}</p>
                                    <p className="designation">{role}</p>
                                    {user.selected_account.name && (
                                        <p className="company">{user.selected_account.name}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="sidebar-content">
                        <List id="sidebar-menu-list">
                            {sidebarLinks.map((listItem, index) => {
                                if (listItem.permission > 0) {
                                    return (
                                        listItem.subListItems ?
                                            <Accordion key={listItem.name} className='menuList' onChange={handleAccordionClick}>
                                                <AccordionSummary
                                                    aria-controls='panel1a-content'
                                                    id='panel1a-header'
                                                    className='w-100 p-0'
                                                    expandIcon={<ChevronRightIcon />}
                                                    onClick={() => {
                                                        if (listItem.path) changeUrl(listItem.path)
                                                        else if (isCollapsed && isDesktopView) setIsCollapsed(false)
                                                    }}
                                                    disabled={listItem.disabled}
                                                    sx={{ height: 'auto' }}
                                                    data-cy={`nav-${listItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                >
                                                    {isCollapsed && isDesktopView ? (
                                                        <Tooltip title={listItem.name} placement="right">
                                                            <ListItemIcon className='ps-2'>{listItem.icon}</ListItemIcon>
                                                        </Tooltip>
                                                    ) : (
                                                        <ListItemIcon className='ps-2'>{listItem.icon}</ListItemIcon>
                                                    )}
                                                    {(!isCollapsed || !isDesktopView) && <Typography className={'ps-2'}>{listItem.name}</Typography>}
                                                </AccordionSummary>
                                                {listItem.subListItems ?
                                                    <AccordionDetails sx={{ p: 0, pl: 1 }}>
                                                        <List className={classes.zeroListPadding}>
                                                            {listItem.subListItems.map((subListItem, index) => {
                                                                if (subListItem.permission > 0) {
                                                                    return (
                                                                        <ListItem
                                                                            key={index}
                                                                            disabled={subListItem.disabled}
                                                                            data-cy={`nav-submenu-${listItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                                            className={(`${window.location.pathname}${window.location.search || ''}`.includes(subListItem.path) && subListItem.path !== '/') || (projectType === subListItem.projectType) ? 'activeList' : ''}
                                                                            onClick={() => changeUrl(subListItem.path, subListItem.notNestedRoute)}>
                                                                            {isCollapsed && isDesktopView ? (
                                                                                <Tooltip title={subListItem.name} placement="right">
                                                                                    <ListItemIcon>{subListItem.icon}</ListItemIcon>
                                                                                </Tooltip>
                                                                            ) : (
                                                                                <ListItemIcon>{subListItem.icon}</ListItemIcon>
                                                                            )}
                                                                            {(!isCollapsed || !isDesktopView) && <ListItemText primary={subListItem.name} />}
                                                                        </ListItem>
                                                                    )
                                                                }
                                                            })}
                                                        </List>
                                                    </AccordionDetails>
                                                    : true}
                                            </Accordion>
                                            :
                                            <ListItem
                                                disabled={listItem.disabled}
                                                onClick={() => changeUrl(listItem.path)}
                                                button
                                                key={listItem.name}
                                                data-cy={`nav-${listItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                className={window.location.pathname.includes(listItem.path) && listItem.path !== '/' ? 'activeList' : ''}
                                            >
                                                {isCollapsed && isDesktopView ? (
                                                    <Tooltip title={listItem.name} placement="right">
                                                        <ListItemIcon>{listItem.icon}</ListItemIcon>
                                                    </Tooltip>
                                                ) : (
                                                    <ListItemIcon>{listItem.icon}</ListItemIcon>
                                                )}
                                                {(!isCollapsed || !isDesktopView) && <ListItemText primary={listItem.name} />}
                                            </ListItem>
                                    )
                                }
                            })}
                            <Accordion className='menuList' onChange={handleAccordionClick}>
                                <AccordionSummary
                                    aria-controls='panel1a-content'
                                    id='panel1a-header'
                                    expandIcon={<ChevronRightIcon />}
                                    sx={{ height: 'auto' }}
                                    onClick={() => {
                                        if (isCollapsed && isDesktopView) setIsCollapsed(false)
                                    }}
                                >
                                    {isCollapsed && isDesktopView ? (
                                        <Tooltip title={`${name} (${role})`} placement="right">
                                            <ListItemIcon className={classes.listItemIconCenter}>
                                                <Avatar alt={name} className={classes.userAvatarSmall} src={require('../../assets/images/avatar/1.jpg').default} />
                                            </ListItemIcon>
                                        </Tooltip>
                                    ) : (
                                        <ListItemIcon className={classes.listItemIconCenter}>
                                            <Avatar alt={name} src={require('../../assets/images/avatar/1.jpg').default} />
                                        </ListItemIcon>
                                    )}
                                    {(!isCollapsed || !isDesktopView) && (
                                        <ListItemText className={classes.userProfileDetails}>
                                            <div>{name}</div>
                                            <div>{role}</div>
                                            {idx(user, _ => _.selected_account.name) && <div>{idx(user, _ => _.selected_account.name)}  </div>}
                                            <div>{user.global_user && <span className="link" onClick={changeAccount}>Change</span>}</div>
                                        </ListItemText>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, pl: 1 }}>
                                    <List className={classes.zeroListPadding}>
                                        <ListItem
                                            data-cy="nav-profile"
                                            className={!(`${window.location.pathname}${window.location.search || ''}`.includes('/profile/reset-password')) && (`${window.location.pathname}${window.location.search || ''}`.includes('/profile')) ? 'activeList' : ''}
                                            onClick={editProfile}>
                                            {isCollapsed && isDesktopView ? (
                                                <Tooltip title="Profile" placement="right">
                                                    <ListItemIcon className="icon-grey"><PersonIcon /></ListItemIcon>
                                                </Tooltip>
                                            ) : (
                                                <ListItemIcon className="icon-grey"><PersonIcon /></ListItemIcon>
                                            )}
                                            {(!isCollapsed || !isDesktopView) && <ListItemText primary={'Profile'} />}
                                        </ListItem>
                                        <ListItem
                                            data-cy="nav-reset-password"
                                            className={(`${window.location.pathname}${window.location.search || ''}`.includes('/profile/reset-password')) ? 'activeList' : ''}
                                            onClick={resetPassword}>
                                            {isCollapsed && isDesktopView ? (
                                                <Tooltip title="Reset Password" placement="right">
                                                    <ListItemIcon><EditIcon /></ListItemIcon>
                                                </Tooltip>
                                            ) : (
                                                <ListItemIcon><EditIcon /></ListItemIcon>
                                            )}
                                            {(!isCollapsed || !isDesktopView) && <ListItemText primary={'Reset Password'} />}
                                        </ListItem>
                                        <ListItem
                                            data-cy="nav-logout"
                                            onClick={logout}>
                                            {isCollapsed && isDesktopView ? (
                                                <Tooltip title="Logout" placement="right">
                                                    <ListItemIcon><LogoutIcon /></ListItemIcon>
                                                </Tooltip>
                                            ) : (
                                                <ListItemIcon><LogoutIcon /></ListItemIcon>
                                            )}
                                            {(!isCollapsed || !isDesktopView) && <ListItemText primary={'Logout'} />}
                                        </ListItem>
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        </List>
                    </div>
                </div>
            </Drawer>
            <main id='layoutAlign' className={
                isDesktopView
                    ? `${classes.content} ${isCollapsed ? 'collapsed-sidebar' : ''}`
                    : 'mobile-content'
            }>
                <div className={classes.toolbar} />
                {props.children}
            </main>
        </div>
    )
}

const mapStateToProps = state => ({
    user: state.user.userData,
    entitlements: state.user.entitlements,
    projectType: state.project.projectType,
    showMobileDrawer: state.common.showMobileDrawer,
    projectCategories: state.project.projectCategories
})

const mapDispatchToProps = dispatch => ({
    saveProjectType: bindActionCreators(saveProjectType, dispatch),
    removeProjectType: bindActionCreators(removeProjectType, dispatch),
    showMobileDrawerAPI: bindActionCreators(showMobileDrawerAPI, dispatch),
    logout: bindActionCreators(logout, dispatch),
    getGlobalSettingReducer: bindActionCreators(getGlobalSettingReducer, dispatch),
    setSidebarCollapsed: bindActionCreators(setSidebarCollapsed, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(HomeLayout)
